// Copyright (c) 2020 WiseTime. All rights reserved.

import { combineReducers, configureStore } from "@reduxjs/toolkit"
import { combineEpics, createEpicMiddleware, StateObservable } from "redux-observable"
import { catchError } from "rxjs/operators"
import { Observable } from "rxjs"
import { enableMapSet } from "immer"
import { Action } from "redux"
import userContextReducer from "features/user-context/slice"
import manualTimeReducer from "features/manual-time/slice"
import tagsReducer from "features/tags/slice"
import activityTypesReducer from "features/activity-types/slice"
import narrativesReducer from "features/narratives/slice"
import timelineReducer from "features/timeline/slices/timeline"
import timelineStatsReducer from "features/timeline/slices/stats"
import timelineAdditionalUiReducer from "features/timeline/slices/additionalUi"
import toolbarReducer from "features/timeline/toolbar/slice"
import trashReducer from "features/timeline/trash/slice"
import userCuesReducer from "features/user-cues/slice"
import narratePreferencesReducer from "features/narrate-preferences/slice"
import userContextEpics from "features/user-context/epics/index"
import timelineEpics from "features/timeline/epics/index"
import toolbarEpics from "features/timeline/toolbar/epics/index"
import manualTimeCommentsEpics from "features/manual-time/epics/index"
import tagsEpics from "features/tags/epics/index"
import activityTypesEpics from "features/activity-types/epics/index"
import narrativesEpics from "features/narratives/epics/index"
import userCuesEpics from "features/user-cues/epics/index"
import narratePreferencesEpics from "features/narrate-preferences/epics/index"

enableMapSet()

export const rootReducer = combineReducers({
  userContext: userContextReducer,
  manualTime: manualTimeReducer,
  tags: tagsReducer,
  activityTypes: activityTypesReducer,
  narratives: narrativesReducer,
  userCues: userCuesReducer,
  timeline: timelineReducer,
  timelineStats: timelineStatsReducer,
  timelineAdditionalUi: timelineAdditionalUiReducer,
  toolbar: toolbarReducer,
  trash: trashReducer,
  narratePreferences: narratePreferencesReducer,
})

export type RootState = ReturnType<typeof rootReducer>

const preventStreamTermination =
  catchError((error, source: Observable<Action>) => {
    console.error(error)
    return source
  })

const rootEpic = (action$: Observable<Action>, state$: StateObservable<RootState>, dependencies: unknown) =>
  combineEpics(
    manualTimeCommentsEpics,
    tagsEpics,
    activityTypesEpics,
    narrativesEpics,
    userContextEpics,
    userCuesEpics,
    timelineEpics,
    toolbarEpics,
    narratePreferencesEpics,
  )(action$, state$, dependencies).pipe(preventStreamTermination)

const epicMiddleware = createEpicMiddleware<Action, Action, RootState, unknown>()

export const store = configureStore({
  reducer: rootReducer,
  middleware: [epicMiddleware],
})

epicMiddleware.run(rootEpic)

